import { useEffect, useRef, useState } from "react";
import { driveApi, type DriveItem } from "@/api/driveApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { categoryOf, extensionOf } from "@/lib/fileTypes";

// Session-wide thumbnail cache. The endpoint needs the bearer header, so
// images come in through fetch → blob URL; keyed by id + updatedAt so an
// edited file gets a fresh thumbnail. Bounded so a huge folder can't leak.

const MAX_ENTRIES = 400;
const pending = new Map<string, Promise<string | null>>();
const ready = new Map<string, string>();

const keyOf = (item: Pick<DriveItem, "id" | "updatedAt">) => `${item.id}:${item.updatedAt}`;

// Mirrors the BFF's DocumentThumbnailer: PDFs and Office files up to 50 MB
// get a first-page thumbnail (generated a few seconds after upload).
const DOCUMENT_THUMB_EXT = new Set(["pdf", "docx", "doc", "docm", "dotx", "rtf", "xlsx", "xls", "xlsm", "pptx", "pptm"]);
const DOCUMENT_THUMB_MAX_BYTES = 50 * 1024 * 1024;

/** True when the BFF renders a thumbnail for this item (image or document). */
export function hasServerThumbnail(item: Pick<DriveItem, "kind" | "name" | "contentType" | "sizeBytes">): boolean {
    if (item.kind !== "File") return false;
    if (categoryOf(item) === "image") return true;
    if (item.sizeBytes <= 0 || item.sizeBytes > DOCUMENT_THUMB_MAX_BYTES) return false;
    return DOCUMENT_THUMB_EXT.has(extensionOf(item.name)) || item.contentType === "application/pdf";
}

/** A document-style thumbnail (a page) rather than a photo. */
export function isDocumentThumbnail(item: Pick<DriveItem, "kind" | "name" | "contentType">): boolean {
    return item.kind === "File" && categoryOf(item) !== "image";
}

export function peekThumbnail(item: Pick<DriveItem, "id" | "updatedAt">): string | undefined {
    return ready.get(keyOf(item));
}

/**
 * Fetches (or reuses) the thumbnail. A 404 means "not generated yet"; pass
 * `retry` to ask again instead of reusing the earlier miss.
 */
export function loadThumbnail(
    item: Pick<DriveItem, "id" | "updatedAt">,
    token: string | undefined,
    opts?: { retry?: boolean }
): Promise<string | null> {
    const key = keyOf(item);
    const hit = ready.get(key);
    if (hit) return Promise.resolve(hit);
    const existing = pending.get(key);
    if (existing && !opts?.retry) return existing;
    const p = fetch(driveApi.thumbnailUrl(item.id), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "omit"
    })
        .then((r) => (r.ok ? r.blob() : null))
        .then((blob) => {
            if (!blob) return null;
            const url = URL.createObjectURL(blob);
            ready.set(key, url);
            return url;
        })
        .catch(() => null);
    pending.set(key, p);
    if (pending.size > MAX_ENTRIES) {
        const oldest = pending.keys().next().value;
        if (oldest !== undefined) {
            pending.delete(oldest);
            const url = ready.get(oldest);
            if (url) {
                ready.delete(oldest);
                URL.revokeObjectURL(url);
            }
        }
    }
    return p;
}

// Thumbnails are generated after the upload returns (documents take a few
// seconds), so a just-changed file asks again on this schedule.
const RETRY_DELAYS_MS = [3_000, 8_000, 20_000, 45_000];
const FRESH_WINDOW_MS = 10 * 60_000;

/** Lazily loads a thumbnail once the element scrolls near the viewport. */
export function useThumbnail<T extends HTMLElement>(item: DriveItem, enabled: boolean) {
    const token = useAccessToken();
    const ref = useRef<T>(null);
    const [src, setSrc] = useState<string | null>(() => (enabled ? peekThumbnail(item) ?? null : null));

    useEffect(() => {
        if (!enabled) {
            setSrc(null);
            return;
        }
        const cached = peekThumbnail(item);
        if (cached) {
            setSrc(cached);
            return;
        }
        const el = ref.current;
        if (!el || typeof IntersectionObserver === "undefined") return;
        let cancelled = false;
        const timers: number[] = [];
        const fresh = Date.now() - Date.parse(item.updatedAt) < FRESH_WINDOW_MS;
        const attempt = (n: number) => {
            void loadThumbnail(item, token, { retry: n > 0 }).then((url) => {
                if (cancelled) return;
                if (url) {
                    setSrc(url);
                    return;
                }
                const delay = RETRY_DELAYS_MS[n];
                if (fresh && delay !== undefined) timers.push(window.setTimeout(() => attempt(n + 1), delay));
            });
        };
        const io = new IntersectionObserver(
            (entries) => {
                if (!entries.some((e) => e.isIntersecting)) return;
                io.disconnect();
                attempt(0);
            },
            { rootMargin: "200px" }
        );
        io.observe(el);
        return () => {
            cancelled = true;
            io.disconnect();
            timers.forEach((t) => window.clearTimeout(t));
        };
        // item identity changes on every refetch; id + updatedAt are what matter.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.id, item.updatedAt, enabled, token]);

    return { ref, src };
}
